"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Sticky public-landing header (2026 refresh). Order follows
 * ui-ux-spec.md's "Public landing page" item 1 (BiteBalance, How it
 * works, Savings, Amy, For employers, DE/EN, Log in, primary CTA), with
 * Contact added after For employers since it now has a real section
 * (item 12) instead of being cut. The BrandMark replaces the plain text
 * wordmark (item 1); the Amy nav item becomes the captivating pill
 * teaser (item 6/7) instead of a plain anchor link.
 */

import { useEffect, useState } from "react"
import { LanguageToggle } from "@/components/layout/LanguageToggle"
import { Button } from "@/components/ui/button"
import { useI18n } from "@/i18n"
import type { AuthMode } from "@/components/auth/authMode"
import { BrandMarkV2 } from "./BrandMarkV2"
import { AskAmyTeaser } from "./AskAmyTeaser"

interface LandingHeaderProps {
  onOpenAuth: (mode: AuthMode) => void
}

const ANCHOR_LINK_CLASS =
  "inline-flex min-h-11 shrink-0 items-center whitespace-nowrap rounded-bb-full px-2.5 text-[13px] font-semibold transition-colors"

/** Section anchors, in page order — one source for the nav AND the scrollspy. */
const NAV_SECTIONS = [
  { id: "how", labelKey: "landingPage.header.navHow" },
  { id: "savings", labelKey: "landingPage.header.navSavings" },
  { id: "demo", labelKey: "landingPage.header.navInside" },
  { id: "pricing", labelKey: "landingPage.header.navPricing" },
  { id: "employers", labelKey: "landingPage.header.navEmployers" },
  { id: "contact", labelKey: "landingPage.header.navContact" },
] as const

/**
 * Scrollspy (2026-08-29 flow pass): the nav link of the section currently
 * in view gets a lime pill, so the header always answers "where am I?".
 * Observation band sits at 25-45% of the viewport — the zone a section
 * occupies right after a smooth anchor scroll settles.
 */
function useActiveSection(): string | null {
  const [active, setActive] = useState<string | null>(null)
  useEffect(() => {
    // Craft fix C4 (2026-09-10): the old callback only ever SET the active id,
    // so scrolling past the last section and back to the hero left "Contact"
    // carrying aria-current="true" forever; and because it took whichever entry
    // the callback happened to iterate last, two sections inside the band made
    // the winner arbitrary. Now the observer keeps a live set of what is in the
    // band and the TOPMOST section in page order wins — with nothing in the
    // band (the hero, the footer) the pill goes away.
    const inBand = new Set<string>()
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) inBand.add(entry.target.id)
          else inBand.delete(entry.target.id)
        }
        setActive(NAV_SECTIONS.find(s => inBand.has(s.id))?.id ?? null)
      },
      { rootMargin: "-25% 0px -55% 0px" }
    )
    for (const { id } of NAV_SECTIONS) {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    }
    return () => observer.disconnect()
  }, [])
  return active
}

export function LandingHeader({ onOpenAuth }: LandingHeaderProps) {
  const { t } = useI18n()
  const activeSection = useActiveSection()

  return (
    <header className="sticky top-0 z-40 border-b border-line bg-paper/95 backdrop-blur supports-[backdrop-filter]:bg-paper/80">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3 md:px-8">
        <a
          href="#top"
          className="flex items-center gap-2 rounded-bb-sm text-lg font-bold text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green"
          aria-label={t("common.appName")}
        >
          <BrandMarkV2 size={30} animated />
          <span>{t("common.appName")}</span>
        </a>

        {/* Owner 2026-08-30: the nav sits INLINE with the logo and actions
            on one aligned row. The 2026-08-29 DE clipping was solved by
            shortening the labels themselves ("So geht's", "Die App",
            "Arbeitgeber" — owner-authorized renames) inside a max-w-7xl
            container, verified in both locales. Below xl the nav drops to
            its own scrollable row so nothing can ever clip on small
            screens. */}
        <nav
          aria-label={t("nav.primaryNavigation")}
          className="order-3 -mx-1 flex w-full items-center overflow-x-auto px-1 pb-1 whitespace-nowrap xl:order-none xl:mx-0 xl:w-auto xl:min-w-0 xl:flex-1 xl:overflow-visible xl:px-0 xl:pb-0"
        >
          <div className="mx-auto flex items-center gap-0.5">
            {NAV_SECTIONS.map(({ id, labelKey }) => (
              <a
                key={id}
                href={`#${id}`}
                aria-current={activeSection === id ? "true" : undefined}
                className={`${ANCHOR_LINK_CLASS} ${
                  activeSection === id
                    ? "bg-lime/60 text-ink"
                    : "text-ink-2 hover:bg-cream hover:text-ink"
                }`}
              >
                {t(labelKey)}
              </a>
            ))}
          </div>
        </nav>

        {/* flex-nowrap + shrink-0: the auth/language cluster must stay one
            line in BOTH locales — DE's longer "Anmelden"/"Registrieren"
            previously wrapped it under the nav. The nav (min-w-0, scrollable)
            absorbs the width pressure instead. */}
        {/* 2026-09-10: in German the action row ("Amy fragen · EN/DE · Anmelden ·
            Registrieren") measures 395px at a 390px viewport, and `shrink-0`
            pushed that 5px onto the document — the page scrolled sideways. It
            now scrolls within its own track, exactly like the nav row above it;
            nothing about the navigation itself changes. */}
        <div className="-mx-1 flex min-w-0 flex-nowrap items-center gap-2 overflow-x-auto px-1 md:mx-0 md:gap-3 md:overflow-visible md:px-0">
          <AskAmyTeaser onOpenAuth={onOpenAuth} compact />
          <LanguageToggle />
          <Button
            type="button"
            variant="ghost"
            className="min-h-11"
            onClick={() => onOpenAuth("signIn")}
          >
            {t("landing.logIn")}
          </Button>
          <Button type="button" className="min-h-11" onClick={() => onOpenAuth("signUp")}>
            {t("landing.signUp")}
          </Button>
        </div>
      </div>
    </header>
  )
}
